/**
 * PostShareModal — UI for the post-level sharing feature (Phase 2,
 * paired with the content-api permissions endpoints).
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────┐
 *   │ "Beitrag teilen"                              [X]   │
 *   ├─────────────────────────────────────────────────────┤
 *   │ Person einladen                                     │
 *   │  [ email / user / agent ▾ ] [ id input ]            │
 *   │  [ read / write ▾ ]            [ Einladen ]         │
 *   ├─────────────────────────────────────────────────────┤
 *   │ Aktuelle Freigaben                                  │
 *   │  • alice@example.com   read   2026-05-27   [⤓] [×]  │
 *   │  • agent:Cloud         write  2026-05-26          [×]│
 *   └─────────────────────────────────────────────────────┘
 *
 * Auth: caller needs to be the post-owner or tenant-admin or
 * super_admin. Backend enforces this; if the user can't grant,
 * we get 403 on list_permissions and surface a friendly message.
 */

import { useEffect, useState } from 'react'
import { CONTENT_API } from '../config'
import { getAccessToken } from '../auth'

type PrincipalType = 'user' | 'agent' | 'email'
type PermissionLevel = 'read' | 'write'

interface PermissionRow {
  id: number
  post_id: number
  principal_type: PrincipalType
  principal_id: string
  permission: PermissionLevel
  granted_by: string
  granted_at: string
  expires_at: string | null
  revoked_at: string | null
  revoked_by: string | null
  share_token: string | null
  share_url: string | null
  metadata_json: Record<string, unknown> | null
}

interface Props {
  postId: number
  open: boolean
  onClose: () => void
}

export function PostShareModal({ postId, open, onClose }: Props) {
  const [rows, setRows] = useState<PermissionRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // New-grant form state
  const [principalType, setPrincipalType] = useState<PrincipalType>('email')
  const [principalRaw, setPrincipalRaw] = useState('')
  const [permission, setPermission] = useState<PermissionLevel>('read')
  const [submitting, setSubmitting] = useState(false)

  const headers = (): Record<string, string> => {
    const tok = getAccessToken()
    return {
      'Content-Type': 'application/json',
      ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
    }
  }

  const reload = async () => {
    if (!open) return
    setLoading(true)
    setError(null)
    try {
      const r = await fetch(
        `${CONTENT_API}/api/v1/posts/${postId}/permissions`,
        { headers: headers() },
      )
      if (r.status === 403) {
        setError(
          'Du darfst keine Freigaben sehen — nur Eigentümer / Tenant-Admin / Super-Admin können das.',
        )
        setRows([])
        return
      }
      if (!r.ok) {
        setError(`HTTP ${r.status}`)
        return
      }
      setRows((await r.json()) as PermissionRow[])
    } catch (e) {
      setError(String((e as Error).message || e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, postId])

  const submitGrant = async () => {
    if (!principalRaw.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      // Auto-prefix the principal_id so the user only has to type
      // the bare email / uuid / agent-name. The backend validator
      // requires the `<type>:` prefix.
      const idRaw = principalRaw.trim()
      const principal_id = idRaw.startsWith(`${principalType}:`)
        ? idRaw
        : `${principalType}:${idRaw}`
      const r = await fetch(
        `${CONTENT_API}/api/v1/posts/${postId}/permissions`,
        {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({
            principal_type: principalType,
            principal_id,
            permission,
            send_email: principalType === 'email',
          }),
        },
      )
      if (!r.ok) {
        const text = await r.text()
        setError(`HTTP ${r.status}: ${text.slice(0, 200)}`)
        return
      }
      setPrincipalRaw('')
      await reload()
    } catch (e) {
      setError(String((e as Error).message || e))
    } finally {
      setSubmitting(false)
    }
  }

  const revoke = async (id: number) => {
    if (!confirm('Freigabe wirklich widerrufen?')) return
    try {
      const r = await fetch(
        `${CONTENT_API}/api/v1/posts/${postId}/permissions/${id}`,
        { method: 'DELETE', headers: headers() },
      )
      if (!r.ok && r.status !== 204) {
        setError(`HTTP ${r.status}`)
        return
      }
      await reload()
    } catch (e) {
      setError(String((e as Error).message || e))
    }
  }

  const togglePermission = async (row: PermissionRow) => {
    const next: PermissionLevel = row.permission === 'read' ? 'write' : 'read'
    try {
      const r = await fetch(
        `${CONTENT_API}/api/v1/posts/${postId}/permissions/${row.id}`,
        {
          method: 'PATCH',
          headers: headers(),
          body: JSON.stringify({ permission: next }),
        },
      )
      if (!r.ok) {
        setError(`HTTP ${r.status}`)
        return
      }
      await reload()
    } catch (e) {
      setError(String((e as Error).message || e))
    }
  }

  if (!open) return null

  return (
    <div
      className="post-share-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        className="post-share-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface, #fff)',
          borderRadius: 12,
          maxWidth: 640,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 12px 48px rgba(0,0,0,0.25)',
          padding: 24,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 16,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 20 }}>Beitrag teilen</h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 0,
              fontSize: 22,
              cursor: 'pointer',
              color: 'var(--text-muted, #888)',
            }}
            aria-label="Schließen"
          >
            ×
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: 10,
              background: '#fee',
              color: '#900',
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}

        {/* ── New-grant form ─────────────────────────────── */}
        <section
          style={{
            marginBottom: 22,
            paddingBottom: 18,
            borderBottom: '1px solid var(--border, #e5e4e7)',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>
            Person einladen
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select
              value={principalType}
              onChange={(e) => setPrincipalType(e.target.value as PrincipalType)}
              style={{ padding: '8px 10px', borderRadius: 6 }}
            >
              <option value="email">Email</option>
              <option value="user">User-UUID</option>
              <option value="agent">Agent-Name</option>
            </select>
            <input
              type={principalType === 'email' ? 'email' : 'text'}
              placeholder={
                principalType === 'email'
                  ? 'alice@example.com'
                  : principalType === 'user'
                    ? 'uuid des users (oder voll: user:<uuid>)'
                    : 'Agent-Name (z.B. Cloud)'
              }
              value={principalRaw}
              onChange={(e) => setPrincipalRaw(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: 6,
                border: '1px solid var(--border, #ccc)',
                flex: '1 1 200px',
                minWidth: 180,
              }}
            />
            <select
              value={permission}
              onChange={(e) => setPermission(e.target.value as PermissionLevel)}
              style={{ padding: '8px 10px', borderRadius: 6 }}
            >
              <option value="read">Lesen</option>
              <option value="write">Schreiben</option>
            </select>
            <button
              onClick={submitGrant}
              disabled={submitting || !principalRaw.trim()}
              style={{
                padding: '8px 14px',
                borderRadius: 6,
                background: 'var(--accent, #4a6cf7)',
                color: 'white',
                border: 0,
                cursor: 'pointer',
                opacity: submitting || !principalRaw.trim() ? 0.5 : 1,
              }}
            >
              {submitting ? '…' : 'Einladen'}
            </button>
          </div>
          {principalType === 'email' && (
            <div
              style={{
                fontSize: 12,
                color: 'var(--text-muted, #888)',
                marginTop: 8,
              }}
            >
              Empfänger bekommt einen Login-Link der den Account automatisch
              anlegt (magic-link, Phase 3).
            </div>
          )}
        </section>

        {/* ── Existing grants ──────────────────────────── */}
        <section>
          <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>
            Aktuelle Freigaben
          </div>
          {loading && <div>Lade…</div>}
          {!loading && rows.length === 0 && !error && (
            <div style={{ color: 'var(--text-muted, #888)', fontSize: 14 }}>
              Noch keine Freigaben. Du, deine Co-Tenant-Members und
              Super-Admins haben sowieso Zugriff.
            </div>
          )}
          {rows.map((row) => (
            <div
              key={row.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 0',
                borderBottom: '1px solid var(--border, #e5e4e7)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={row.principal_id}
                >
                  {prettyPrincipal(row.principal_id, row.principal_type)}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-muted, #888)',
                  }}
                >
                  Eingeladen {new Date(row.granted_at).toLocaleString('de-DE')}
                  {row.expires_at &&
                    ` · läuft ab ${new Date(row.expires_at).toLocaleDateString('de-DE')}`}
                </div>
                {row.share_url && (
                  <div style={{ fontSize: 11, marginTop: 4 }}>
                    <button
                      onClick={() => {
                        void navigator.clipboard.writeText(row.share_url!)
                      }}
                      style={{
                        background: 'transparent',
                        border: '1px solid var(--border, #ccc)',
                        borderRadius: 4,
                        padding: '2px 8px',
                        cursor: 'pointer',
                        fontSize: 11,
                      }}
                    >
                      Link kopieren
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => togglePermission(row)}
                title={
                  row.permission === 'read'
                    ? 'Klick: zu Schreiben hochstufen'
                    : 'Klick: auf Lesen herabstufen'
                }
                style={{
                  padding: '4px 10px',
                  borderRadius: 4,
                  fontSize: 12,
                  background:
                    row.permission === 'write'
                      ? '#d4edda'
                      : 'var(--surface-alt, #f5f5f5)',
                  border: '1px solid var(--border, #ccc)',
                  cursor: 'pointer',
                }}
              >
                {row.permission === 'write' ? 'Schreiben' : 'Lesen'}
              </button>
              <button
                onClick={() => revoke(row.id)}
                title="Widerrufen"
                style={{
                  padding: '4px 8px',
                  background: 'transparent',
                  border: 0,
                  cursor: 'pointer',
                  color: '#c33',
                  fontSize: 16,
                }}
              >
                ×
              </button>
            </div>
          ))}
        </section>
      </div>
    </div>
  )
}

function prettyPrincipal(pid: string, ptype: PrincipalType): string {
  if (pid.startsWith(`${ptype}:`)) {
    const v = pid.slice(ptype.length + 1)
    if (ptype === 'email') return v
    if (ptype === 'agent') return `🤖 ${v}`
    return `👤 ${v.slice(0, 12)}…`
  }
  return pid
}
